// 11 services — same sector as Cito (technology & internet services),
// expanded and rewritten for Atlas Brokerage.
const services = [
  {
    slug: "crm",
    title: "CRM Solutions",
    short: "Customer relationship platforms that turn fragmented data into measurable revenue.",
    summary:
      "We deploy, customise and integrate Customer Relationship Management platforms that connect every commercial conversation — from the first lead to long-term renewal.",
    icon: "people",
    paragraphs: [
      "Modern CRM is no longer a contact database. It is the operating system of a commercial organisation: the place where marketing campaigns, sales pipelines, customer support and retention loops converge. Atlas Brokerage helps companies move from spreadsheets and disconnected tools to a single, trusted source of truth.",
      "Our practice covers strategy, platform selection, technical implementation and post-launch enablement. We work with both leading vendor platforms and bespoke builds, and we specialise in the integrations that decide whether a CRM actually gets used — telephony, e-mail, billing, ticketing, marketing automation and analytics.",
      "Every engagement starts with a process audit. We map your real-world customer journeys, identify the moments where data is lost or duplicated, and design a CRM blueprint that reflects how your teams actually sell and serve. The deployment phase then becomes a series of confident, low-risk increments rather than a multi-year transformation."
    ],
    bullets: [
      "Sales pipeline, marketing and service modules unified on a single platform",
      "Native integration with telephony, e-mail, ERP and accounting systems",
      "Custom dashboards, KPIs and forecasts tailored to your reporting model",
      "Role-based access, audit trails and GDPR-aligned data governance",
      "User onboarding, training programmes and ongoing administrator support"
    ]
  },
  {
    slug: "software-development",
    title: "Custom Software Development",
    short: "Web, mobile and back-office applications engineered for your exact operating model.",
    summary:
      "When packaged software stops fitting the way you work, we build the application that does — from internal portals to customer-facing web platforms.",
    icon: "code",
    paragraphs: [
      "Software is the most direct expression of how a business chooses to work. Off-the-shelf tools cover the obvious patterns, but every serious organisation reaches a point where its differentiating processes deserve software designed around them rather than the other way around.",
      "Atlas Brokerage builds web applications, internal tools, customer portals, mobile companion apps and integration layers using a modern, mainstream technology stack. We deliver in short, demonstrable iterations so that stakeholders see working software early and can steer the product before it has solidified.",
      "Our engineers work across the full lifecycle — discovery and UX, architecture, implementation, automated testing, deployment pipelines and post-launch evolution. Whether you need a focused MVP or a long-running platform team, we structure the engagement to match the maturity of the product."
    ],
    bullets: [
      "Discovery workshops, product blueprints and clickable prototypes",
      "React, Node, .NET, Python and mobile (React Native / iOS / Android) stacks",
      "API-first architecture, third-party integrations and webhooks",
      "Automated test suites, CI/CD pipelines and observability from day one",
      "Long-term maintenance, SLA-backed support and feature roadmap delivery"
    ]
  },
  {
    slug: "network",
    title: "Network Infrastructure",
    short: "Wired, wireless and SD-WAN networks designed for performance you can actually measure.",
    summary:
      "From a single office to multi-site enterprise networks, we plan, install and operate the connectivity layer that keeps everything else running.",
    icon: "network",
    paragraphs: [
      "A network either disappears or it becomes the single biggest source of friction in a business. Atlas Brokerage designs networks that disappear: predictable, resilient and instrumented so that problems are visible long before users feel them.",
      "Our engineers handle the full stack — structured cabling, switching and routing, enterprise Wi-Fi, segmentation, SD-WAN between sites and secure remote access for distributed teams. We are vendor-pragmatic: we choose hardware and software based on what your environment actually needs, not on certifications we want to renew.",
      "Every network we deploy is documented to the port level and handed over with monitoring already in place. Customers know what they own, where it lives and how it is performing — three things that are surprisingly rare in the field."
    ],
    bullets: [
      "Site surveys, capacity planning and structured cabling design",
      "Enterprise-grade switching, routing and high-density Wi-Fi 6 / 6E",
      "SD-WAN, MPLS alternatives and resilient multi-site connectivity",
      "VLAN segmentation, zero-trust access and secure remote VPN",
      "End-to-end documentation, monitoring and proactive alerting"
    ]
  },
  {
    slug: "cloud-pbx",
    title: "Cloud PBX & IP Telephony",
    short: "Modern voice systems that work from anywhere, integrate with everything and cost less than legacy lines.",
    summary:
      "IP-based switchboard and contact-centre platforms that connect mobile, desktop and desk phones into one intelligent voice system.",
    icon: "phone",
    paragraphs: [
      "Voice is still the channel where customers expect a person and where lost calls translate directly into lost revenue. The technology underneath, however, has fundamentally changed. Traditional PBX systems are being replaced by IP-based platforms that follow users instead of being tied to a building.",
      "Atlas Brokerage deploys cloud and hybrid PBX systems that route calls intelligently across mobile applications, soft-phones, desk phones and contact-centre queues. Numbers are portable, recordings are searchable and every conversation is available to your CRM the moment it ends.",
      "We support businesses through the full transition: number porting, SIP trunk procurement, hardware sourcing, dial-plan design, IVR scripting and the inevitable iteration after go-live. The result is a phone system your staff actually want to use."
    ],
    bullets: [
      "Cloud-hosted and on-premise IP PBX deployments",
      "SIP trunking, number porting and international DID management",
      "Mobile, desktop and physical handset clients on a single extension",
      "IVR menus, call queues, recording, transcription and analytics",
      "Native integration with CRM, helpdesk and ticketing systems"
    ]
  },
  {
    slug: "cyber-security",
    title: "Cyber Security",
    short: "Defensive security programmes that match the reality of a permanently hostile internet.",
    summary:
      "We harden, monitor and respond — covering endpoints, networks, identities and cloud workloads against modern threats.",
    icon: "shield",
    paragraphs: [
      "Cyber security is no longer an annual project. It is a continuous practice that has to keep pace with threats that change weekly. Atlas Brokerage runs that practice on behalf of organisations that need real defensive posture without building an in-house security team from scratch.",
      "We start with a defensible baseline: asset inventory, identity hygiene, e-mail and endpoint protection, patch discipline and segmentation. From there we layer the controls that match your risk profile — managed detection and response, vulnerability management, phishing simulation, secure remote access and DLP.",
      "When something does happen, we are the team you call. Our incident response process is rehearsed, documented and bounded by clear SLAs, so that a bad day stays a bad day rather than becoming an existential one."
    ],
    bullets: [
      "Next-generation firewalls, EDR, e-mail security and Wi-Fi hardening",
      "Identity and access management, MFA and conditional access policies",
      "Vulnerability scanning, patch management and penetration testing",
      "24/7 monitoring, threat hunting and incident response on retainer",
      "Security awareness training and phishing simulation programmes"
    ]
  },
  {
    slug: "project-management",
    title: "Project Management",
    short: "Senior delivery management for the technology programmes you cannot afford to drift.",
    summary:
      "We bring structured project management to complex technology initiatives — scope, schedule, cost and risk under one accountable lead.",
    icon: "tasks",
    paragraphs: [
      "Technology programmes fail far more often from delivery discipline than from technical complexity. Atlas Brokerage provides senior project and programme managers who own the schedule, the budget, the dependencies and the difficult conversations.",
      "We work in whatever method fits the engagement — predictable waterfall for regulated programmes, Scrum or Kanban for product work, hybrid for the realistic middle ground that most organisations live in. The constants are clear status reporting, honest risk registers and a single point of accountability.",
      "Our PMs come from the engineering side, not the spreadsheet side. They understand what they are managing, which means they negotiate scope on facts and not on slogans, and they catch issues months before they show up on a steering-committee dashboard."
    ],
    bullets: [
      "Programme initiation, business case shaping and stakeholder alignment",
      "Detailed plans, milestone tracking, RAID logs and steering reports",
      "Vendor management, contract negotiation and integration coordination",
      "Risk, quality, change and supplier management as continuous practices",
      "PMO setup, governance frameworks and post-implementation review"
    ]
  },
  {
    slug: "web-hosting",
    title: "Web Hosting & Domains",
    short: "Hosting infrastructure, domains, SSL and DNS — quietly reliable, fully managed.",
    summary:
      "We host and operate websites, e-mail and DNS zones on infrastructure we monitor around the clock.",
    icon: "globe",
    paragraphs: [
      "Hosting feels like a commodity right up until something breaks at the wrong moment. Atlas Brokerage runs hosting that doesn't break at the wrong moment — and when something does need attention, you have a real engineer responding, not a ticketing portal.",
      "We provide managed hosting for marketing sites, content platforms, e-commerce and custom web applications, on infrastructure ranging from shared environments through dedicated VMs to fully isolated cloud accounts. We also handle the unglamorous but business-critical parts: domain registration, DNS management, SSL renewals and backup verification.",
      "Every hosted service we operate is backed by uptime monitoring, off-site backups and a documented recovery procedure. That sounds standard. In practice, it is the thing that decides whether an incident is a half-hour story or a half-week one."
    ],
    bullets: [
      "Managed shared, VPS, dedicated and cloud hosting tiers",
      "Domain registration, DNS, SSL/TLS and CDN configuration",
      "Daily off-site backups with documented restore procedures",
      "Performance tuning, caching layers and core-web-vitals optimisation",
      "Uptime monitoring with human escalation 24/7"
    ]
  },
  {
    slug: "cloud-devops",
    title: "Cloud & DevOps",
    short: "Cloud architectures and delivery pipelines that move teams from monthly releases to daily ones.",
    summary:
      "We design, migrate and operate workloads on AWS, Azure and Google Cloud — automated, observable and cost-aware.",
    icon: "cloud",
    paragraphs: [
      "Cloud only pays back when it is treated as an engineering discipline rather than a hosting choice. Atlas Brokerage helps organisations land on AWS, Azure or Google Cloud in a way that compounds — infrastructure as code, automated deployments, observability and cost controls in place from day one.",
      "We work end-to-end: workload assessment, target architecture, migration execution, platform engineering and ongoing operations. For greenfield builds we set up the foundations — networking, identity, secrets, CI/CD, logging — that make later teams faster instead of slower.",
      "Our DevOps engagements are pragmatic. We bring strong opinions about automation and reliability, but we tune the practices to your team's size and maturity. The goal is always the same: smaller releases, shorter recovery, clearer ownership."
    ],
    bullets: [
      "Cloud landing zones on AWS, Azure and GCP with IaC (Terraform / Bicep)",
      "Lift-and-shift, re-platforming and re-architecting migration paths",
      "CI/CD pipelines, container platforms (Kubernetes, ECS, AKS) and GitOps",
      "Observability stacks: metrics, structured logs, traces and SLO tracking",
      "FinOps practices, cost dashboards and right-sizing programmes"
    ]
  },
  {
    slug: "data-analytics",
    title: "Data Analytics & Business Intelligence",
    short: "Pipelines, warehouses and dashboards that turn operational data into business decisions.",
    summary:
      "From the first ETL pipeline to a full data platform, we build the analytics layer that makes leadership decisions evidence-based.",
    icon: "chart",
    paragraphs: [
      "Every business already produces enough data to make better decisions. The gap is rarely the data itself — it is the pipelines, definitions and presentation layers that turn raw events into something a board can read on a Monday morning.",
      "Atlas Brokerage builds data platforms that close that gap. We design data warehouses, implement ingestion from your operational systems, model the metrics that matter and deliver dashboards in tools your teams will actually open. Where it is justified, we layer machine-learning use cases on top — forecasting, segmentation, anomaly detection.",
      "We are deliberate about scope. A first phase usually targets three to five business questions, ships in weeks rather than quarters and earns the right to expand. The alternative — a multi-year data programme with no visible product — is the failure pattern we are explicitly built to avoid."
    ],
    bullets: [
      "Cloud data warehouses on Snowflake, BigQuery and Redshift",
      "ELT pipelines (dbt, Fivetran, Airbyte) and reverse-ETL workflows",
      "Semantic layer modelling, KPI definitions and data governance",
      "Dashboards in Power BI, Looker, Tableau and Metabase",
      "Forecasting, segmentation and anomaly-detection models"
    ]
  },
  {
    slug: "it-consulting",
    title: "IT Consulting & Architecture",
    short: "Independent technology advisory for boards, founders and IT leaders who need clarity, not vendor pitches.",
    summary:
      "Strategy, architecture and technology due diligence delivered by senior practitioners with no vendor agenda.",
    icon: "compass",
    paragraphs: [
      "Most technology decisions are made under uncertainty, under time pressure and under the influence of the loudest vendor in the room. Atlas Brokerage offers independent, senior consulting precisely for the moments where those decisions matter most.",
      "Typical engagements include technology strategy alignment, target-state architecture, vendor selection, build-versus-buy analysis, technical due diligence for investments and post-merger IT integration planning. We deliver written recommendations that a board can read, defended by analysis that an engineering team can verify.",
      "Our consultants have built and run the systems they advise on. That means our recommendations come with realistic timelines and honest trade-offs, not with the suspiciously confident slides that show up when accountability lies somewhere else."
    ],
    bullets: [
      "Technology strategy, target operating model and architecture roadmaps",
      "Vendor evaluation, RFP design and contract negotiation support",
      "Technical due diligence for M&A and investment decisions",
      "Post-merger IT integration and rationalisation planning",
      "Fractional CTO and senior advisory retainers"
    ]
  },
  {
    slug: "managed-it",
    title: "Managed IT Services",
    short: "Outsourced IT operations for organisations that need an enterprise-grade IT function without the headcount.",
    summary:
      "A complete external IT department — service desk, infrastructure operations, vendor management and reporting under one SLA.",
    icon: "support",
    paragraphs: [
      "Building a high-functioning internal IT team is expensive, slow and difficult to retain. For most mid-market businesses, the better answer is a managed service partner that delivers the same outcomes against contracted service levels.",
      "Atlas Brokerage operates as your IT department: a tiered service desk for user requests, proactive infrastructure monitoring, patch and asset management, vendor coordination for connectivity and software, and monthly reporting that translates IT activity into business language.",
      "Engagements are scoped against measurable SLAs — response times, resolution targets, uptime, satisfaction — and reviewed quarterly. As your business grows, the service grows with it; if you decide later to bring IT back in-house, we hand over a documented, defensible operating model rather than a black box."
    ],
    bullets: [
      "Tier-1 / Tier-2 service desk with multilingual support",
      "Endpoint management, patching, anti-virus and asset tracking",
      "Server, network and cloud infrastructure monitoring and operations",
      "Vendor management for ISPs, SaaS subscriptions and hardware",
      "Quarterly business reviews with KPIs, risks and improvement plans"
    ]
  }
];

export default services;

export const getServiceBySlug = (slug) =>
  services.find((s) => s.slug === slug) || null;
